import React, { useEffect, useState } from 'react'
import { Anchor, Box, Button, Group, Popover, Stack, Text, Title, Tooltip, UnstyledButton, rem } from '@mantine/core'
import { useSelector } from 'react-redux'
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { notifications } from '@mantine/notifications';
import { useDispatch } from 'react-redux'
import { aiMatchingGridActions } from './aiSurveyMatchGridCreate'
import { applySearchFilters, hasRequiredFilters, isChecked, isRowLoading, useSelectAccessor } from './helpers';
import { IconFilter } from '@tabler/icons-react';
import { FiltersModal } from './FiltersModal/FiltersModal';
import { jobMatchGridActions } from '../LandingPage/JobMatchDetail/JobMatchGridCreate';
import { addMatch, deleteMatch } from '../MarketPricingActions';
import { addSelectedCutID, removeSelectedCutID } from '../JobPricing/JobPricingActions';
import GG_DEFAULTS from '../../../lib/genericGridDefaults';
import { IAppState, FilterGroup, SearchFilter, VectorSearchFilters } from '../../../lib/types';
import { useLocalStorage } from '@mantine/hooks';
import { SelectedFilters } from './SelectedFilters/SelectedFilters';
import { useHotkeys } from '@mantine/hooks';
import { HistogramModal } from './HistogramModal';
import {useTypeAheadFilters} from '../../../hooks/ai/use-type-ahead-filters'

type Props = {
  state: any
  isReadOnly: boolean
  handleOnMatchSelect: any
  selectedViewPk: number
  showSurveyDataCutsSlideout: (survey: any) => void
  onMatchSelect: (index: number, isNew: boolean) => void
  updateModifiedDate: (jobId: number, benchmarkYear: number) => void
  getSurveyJobDetailsPageHref: (cutId: number) => void
}

export const AIMatching = ({ state, onMatchSelect, getSurveyJobDetailsPageHref, showSurveyDataCutsSlideout, updateModifiedDate, selectedViewPk }: Props) => {
  const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false)
  const [histogramModalOpen, setHistogramModalOpen] = useState<boolean>(false)
  const [matches, setMatches] = useState<Record<number, boolean>>({});
  const [addedMatches, setAddedMatches] = useState<number[]>([]);
  const dispatch = useDispatch()
  const { currency, currentJob } = state
  const { title, description } = state.currentJob
  const marketPricing = useSelector((state: IAppState) => state.marketPricing)
  const aiSurveyMatchGrid = useSelector((state: any) => state.marketPricing.AIMatching.aiSurveyMatchGrid)
  const { selectedBenchmarkYear, selectedCutIDs } = marketPricing.JobPricingReducer.MainJobPricingReducer
  const { loadingId, isMatchDeleting, displayIn } = marketPricing.MarketPricingReducer
  const { tableProps, loading, loaded, error, metadata: { limit = 50, offset = 0, histogram = { count: [], binEdges: [] } } } = aiSurveyMatchGrid
  const session = useSelector((state: IAppState) => state.session)
  const [localStorageLoaded, setLocalStorageLoaded] = useState<boolean>(false)
  const maid = session.organization.masterAccountId
  const { numericRounding, percentRounding, monetaryRounding } = session.user.preferences
  
  const [selectedFilters, setSelectedFilters] = useLocalStorage<VectorSearchFilters>({ key: 'vectorSearchFiltersV2', defaultValue: { jobTitle: title || '', currency: [currency], jobDescription: '', publisher: null, surveyCodeSurveyShortName: null}})

  const loadLocalStorage = () => {
    setSelectedFilters(prev => ({ ...prev, jobTitle: title || '', jobDescription: description || '', currency: [currency] }))
    setLocalStorageLoaded(true)
  }

  useHotkeys([
    ['ctrl+shift+X', () => dispatch(aiMatchingGridActions.toggleSimilarityDisplay())],
    ['ctrl+shift+H', () => !!histogram.binEdges.length && !!histogram.counts.length ? setHistogramModalOpen(prev => !prev) : undefined]
  ]);

  const handleSetAction = (newSetValue: any, cutId: number) => {
    dispatch(jobMatchGridActions.setIsStale(currentJob.id, true));
    if (newSetValue === true) {
      return dispatch(addMatch(currentJob.id, cutId, null, false, selectedBenchmarkYear, null))
        .then(() => {dispatch(addSelectedCutID(cutId))})
        // .then(() => captureFiltersOnMatch(currentJob.id, cutId, selectedBenchmarkYear));
    } else {
      return dispatch(deleteMatch(currentJob.id, [cutId], null, selectedBenchmarkYear, false, null))
        .then(() => dispatch(removeSelectedCutID(cutId)));
    }
  }

  const setSelectedCut = (selectedMatch: number) => {
    const cutId = selectedMatch;
    const newSetValue = !matches[cutId];
    const cutIdIndex = aiSurveyMatchGrid.terms.findIndex((term: any) => term.term === GG_DEFAULTS.CUT_ID);
    const currencyIdIndex = aiSurveyMatchGrid.terms.findIndex((term: any) => term.term === GG_DEFAULTS.CURRENCY);
    const foundItem = aiSurveyMatchGrid.data.find((el: any) => el[cutIdIndex] === selectedMatch);

    handleSetAction(newSetValue, cutId).then(() => {
      updateModifiedDate(currentJob.id, selectedBenchmarkYear);
      matches[cutId] = newSetValue;
      const newAddedMatches: number[] = addedMatches;

      !!newSetValue ? newAddedMatches.push(cutId) : newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1)

      setAddedMatches(newAddedMatches);
      onMatchSelect(foundItem[currencyIdIndex], newSetValue !== true);
    });
  }

  const handleFiltersChange = (filters: VectorSearchFilters) => {
    setSelectedFilters(filters)
    if (hasRequiredFilters(filters)) {
      const searchFilters = applySearchFilters(limit, offset, displayIn, selectedBenchmarkYear, filters)
      dispatch(aiMatchingGridActions.search(`/cms/api/accounts/${maid}/survey-jobs`, searchFilters, 'post'))
    }
  }

  const handleRemove = (term: FilterGroup | 'jobTitle' | 'jobDescription', value: string) => {
    if (term === 'jobTitle' || term === 'jobDescription') {
      const newFilters = term === 'jobTitle' ? { ...selectedFilters, jobTitle: '' } : { ...selectedFilters, jobDescription: '' }
      handleFiltersChange(newFilters)
    } else {
      const newFilters = selectedFilters[term]?.length === 1 ?
      { ...selectedFilters, [term]: null } :
      { ...selectedFilters, [term]: (selectedFilters[term] || []).filter((sf: FilterGroup) => sf !== value) }
      handleFiltersChange(newFilters)
    }
  }

  const handleAdd = (term: FilterGroup | 'jobTitle' | 'jobDescription', value: string) => {
    const newFilters = ['jobTitle', 'jobDescription'].includes(term) ?
      { ...selectedFilters, [term]: value } :
      { ...selectedFilters, [term]: Array.isArray(selectedFilters[term]) ? [...selectedFilters[term] as string[], value] : [value] }
    handleFiltersChange(newFilters)
  }

  const {data: searchFilters} = useTypeAheadFilters(currency, selectedFilters)

  // Store the filters that were used while adding a match to reinforce the AI model
  // const captureFiltersOnMatch = async (org_job_id: number , cutId: number, selectedBenchmarkYear: number): Promise<boolean> => {
  //   try {
  //    await axios.apiPost(`/cms/api/accounts/${maid}/filters/capture-filters-on-match`, {
  //      org_job_id,
  //      cut_id: cutId,
  //      benchmarkYear: selectedBenchmarkYear,
  //      selectedFilters
  //   });
  //   } catch (error) {
  //     // silent error -- optimsitic update. 
  //   }
  //   return true
  // }

  useEffect(() => {
    if (error) notifications.show({ title: 'Service Unavailable', message: 'AI Search is temporarily unavailable.', color: 'red', autoClose: 3000, sx: { fontSize: 18 } })
  }, [error])

  useEffect(() => {
    loadLocalStorage()
    const searchFilters = applySearchFilters(limit, 0, displayIn, selectedBenchmarkYear, selectedFilters || [])

    if (selectedViewPk && hasRequiredFilters(selectedFilters) && localStorageLoaded) {
      dispatch(aiMatchingGridActions.search(`/cms/api/accounts/${maid}/survey-jobs`, searchFilters, 'post'))
    }
  }, [currentJob, selectedViewPk, displayIn, localStorageLoaded])

  useEffect(() => {
    const selected: Record<number, boolean> = {}
    selectedCutIDs?.forEach((id: number) => {
      selected[id] = true;
      setMatches(selected);
    });
  }, [selectedCutIDs])


  return (
    <Box>
      <HistogramModal opened={histogramModalOpen} onClose={() => setHistogramModalOpen(false)} histogram={histogram} />
      {searchModalOpen && searchFilters &&
        <FiltersModal
          opened={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFiltersChange}
          currency={currency}
        />
      }
      {!hasRequiredFilters(selectedFilters) ?
        <Stack h={900}>
          <Title ta='center' mt={75} order={5}>
            Job title or description is required. Apply <Text onClick={() => setSearchModalOpen(true)} c='blue' style={{ cursor: 'pointer' }} span inherit>filters</Text> to begin searching survey jobs.
          </Title>
        </Stack> :
      <Stack h='calc(100vh - 230px' w='100%' gap={0}>
        <Group p='sm' justify='space-between' align='flex-start' wrap='nowrap'>
          <Group>
            <Tooltip label='Add/Edit Filters'>
              <Button variant='outline' leftSection={<IconFilter style={{ width: rem(18), height: rem(18) }}/>} onClick={() => setSearchModalOpen(true)}>
                Filters
              </Button>
            </Tooltip>
            <SelectedFilters 
              searchFilters={searchFilters}
              selectedFilters={selectedFilters}
              onAdd={handleAdd}
              onRemove={handleRemove}
              currency={currency}
            />
          </Group>
          <Group mr="sm">
            <Popover width={600} withArrow arrowSize={12}>
              <Popover.Target>
                <UnstyledButton c='blue' style={{ textWrap: 'nowrap' }}>What is AI Matching?</UnstyledButton>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="sm">
                    This feature uses an artificial intelligence (“AI”) model and is subject to terms included in the <Anchor href='https://www.payscale.com/about/documentation' target='_blank'>Documentation</Anchor>.
                    Use of this feature is optional and provides for human review and approval of any suggestions.
                </Text>
              </Popover.Dropdown>
            </Popover>
          </Group>
        </Group>
        <Stack h='100%' justify={tableProps.dataLoading && !tableProps.columnHeaders.length ? 'center' : 'flex-start'} style={{ overflow: 'scroll' }}>
          <AdvancedTable
            {...tableProps}
            {...aiMatchingGridActions}
            getDataRow={(row: any, idx: any) =>
              getGenericRow(row, idx, {
                onClick: null,
                isRowLoading: isRowLoading(row, loadingId),
                isRowDeleting: isMatchDeleting,
                loadingText: 'Adding Match to Pricing...',
                deletingText: 'Removing Match from Pricing...',
                addedMatches: [],
                onDataCutClick: showSurveyDataCutsSlideout,
                getSurveyJobDetailsPageHref,
                numericRounding,
                percentRounding,
                monetaryRounding
              })
            }
            useSelectAll={false}
            isAllChecked={false}
            stickyHeader={true}
            isChecked={(cutId: number) => isChecked(cutId, matches)}
            onCheck={setSelectedCut}
            useSelectAccessor={useSelectAccessor}
            noDataText={'No Surveys Found'}
            loadingText={'Loading Survey Job Matches'}
            dataLoading={loading || !loaded}
            loadingId={loadingId}
            allowResize={true}
            allowSorting={true}
            useClientSideSort={true}
          />
        </Stack>
      </Stack>
      }
    </Box>
  )
}
